import { useStore } from '../store'
import { detectSilences } from './silence'
import type { FormatKey } from './formats'

/**
 * Chat-to-edit: diễn giải câu lệnh ngôn ngữ tự nhiên (Việt/Anh) thành thao tác
 * chỉnh sửa. Dùng luật/khớp từ khóa (chạy offline, không cần LLM API).
 */
export function runCommand(text: string): string {
  const t = text.toLowerCase().trim()
  if (!t) return 'Hãy nhập một lệnh, ví dụ: "xóa từ đệm", "xóa khoảng lặng", "cắt còn 60 giây".'

  const s = useStore.getState()
  const clip = s.clips.find((c) => c.id === s.activeClipId)

  // Khôi phục / hoàn tác
  if (/khôi phục|hoàn tác|undo|reset|hoàn nguyên/.test(t)) {
    s.restoreAll()
    return '↺ Đã khôi phục toàn bộ chỉnh sửa của clip này.'
  }

  // Xóa từ đệm
  if (/từ đệm|filler|ậm ừ/.test(t)) {
    if (!clip?.transcribed) return '⚠️ Cần phiên âm clip trước đã.'
    s.removeAllFillers()
    return '✂️ Đã cắt tất cả từ đệm.'
  }

  // Xóa khoảng lặng
  if (/khoảng lặng|im lặng|khoảng trống|silence|dead ?space|chỗ lặng/.test(t)) {
    if (!clip?.transcribed) return '⚠️ Cần phiên âm clip trước để biết chỗ lặng.'
    const cuts = detectSilences(clip.words)
    if (cuts.length === 0) return 'Không thấy khoảng lặng đáng kể.'
    s.addCuts(clip.id, cuts)
    return `🔇 Đã cắt ${cuts.length} khoảng lặng.`
  }

  // Cắt còn N giây / phút
  const dur = parseDuration(t)
  if (dur !== null && /(cắt|còn|trim|rút|ngắn|độ dài|dài)/.test(t)) {
    if (!clip) return '⚠️ Chưa có clip.'
    s.trimActiveTo(dur)
    return `⏱️ Đã cắt bản dựng còn ~${dur} giây (clip này).`
  }

  // Chọn tỉ lệ xuất
  const fmt = matchFormat(t)
  if (fmt) {
    if (!s.exportFormats.includes(fmt.key)) s.toggleFormat(fmt.key)
    return `📐 Đã chọn tỉ lệ ${fmt.label}. Bấm "Xuất" khi xong.`
  }

  // Áp Style DNA
  if (/áp.*(style|phong cách|màu)|phong cách|style dna|đồng bộ màu/.test(t)) {
    if (!s.style.ref) return '⚠️ Chưa có Style DNA — trích xuất từ video mẫu trước (bảng 🎨).'
    if (!s.style.apply) s.toggleStyleApply()
    return '🎨 Đã bật áp Style DNA (chất màu theo video mẫu).'
  }

  // Studio audio
  if (/lọc.*(âm|tiếng|ồn)|khử ồn|studio|audio|âm thanh/.test(t)) {
    s.toggleStudioAudio()
    return `🎧 Studio audio: ${useStore.getState().studioAudio ? 'BẬT' : 'TẮT'} (áp khi xuất).`
  }

  return 'Chưa hiểu lệnh. Thử: "xóa từ đệm", "xóa khoảng lặng", "cắt còn 45 giây", "xuất dọc 9:16", "áp phong cách", "lọc ồn".'
}

function parseDuration(t: string): number | null {
  let m = t.match(/(\d+(?:[.,]\d+)?)\s*(phút|phut|min|minute|m\b)/)
  if (m) return Math.round(parseFloat(m[1].replace(',', '.')) * 60)
  m = t.match(/(\d+(?:[.,]\d+)?)\s*(giây|giay|s\b|sec|second)/)
  if (m) return Math.round(parseFloat(m[1].replace(',', '.')))
  m = t.match(/còn\s+(\d+)/)
  if (m) return parseInt(m[1], 10)
  return null
}

function matchFormat(t: string): { key: FormatKey; label: string } | null {
  if (/dọc|9:16|9x16|tiktok|reels|short/.test(t)) return { key: 'r9x16', label: '9:16 Dọc' }
  if (/ngang|16:9|16x9|youtube/.test(t)) return { key: 'r16x9', label: '16:9 Ngang' }
  if (/vuông|vuong|1:1|1x1/.test(t)) return { key: 'r1x1', label: '1:1 Vuông' }
  if (/4:5|4x5/.test(t)) return { key: 'r4x5', label: '4:5' }
  return null
}
